import { useState } from "react";
import { toast } from "sonner";
import { Printer } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { BrandButton } from "@/components/PageHeader";
import { ChequeReceipt, ChequeStatus } from "@/components/payments/ChequeDialogs";
import { api, cedi2 as cedi, errMsg, fmtDate } from "@/lib/api";

export const ChequeReceiptDialog = ({ c, onClose }) => (
  <Dialog open={!!c} onOpenChange={onClose}>
    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="cheque-receipt-dialog">
      <DialogHeader><DialogTitle className="sr-only">Cheque receipt</DialogTitle></DialogHeader>
      {c && <ChequeReceipt c={c} />}
      <BrandButton variant="dark" onClick={() => window.print()} data-testid="cheque-receipt-print"><Printer className="w-4 h-4" />Print receipt</BrandButton>
    </DialogContent>
  </Dialog>
);

const ConfirmSettle = ({ ask, onClose, onConfirm }) => (
  <AlertDialog open={!!ask} onOpenChange={onClose}>
    <AlertDialogContent data-testid="cheque-settle-dialog">
      <AlertDialogHeader>
        <AlertDialogTitle>{ask?.status === "Cleared" ? "Mark cheque as cleared?" : "Mark cheque as bounced?"}</AlertDialogTitle>
        <AlertDialogDescription>{ask?.status === "Cleared" ? `${cedi(ask?.c.amount)} will be added as paid for ${ask?.c.shares.length} participant(s), and their payment alerts will be sent.` : "No payment will be counted. The participants keep their balance and can pay another way."}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="cheque-settle-cancel">Cancel</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} data-testid="cheque-settle-confirm">{ask?.status === "Cleared" ? "Mark cleared" : "Mark bounced"}</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const Row = ({ c, canAct, onAsk, onView }) => (
  <tr className="align-top" data-testid={`cheque-row-${c.cheque_no}`}>
    <td className="px-4 py-3 font-mono text-xs"><div className="font-semibold">{c.cheque_no}</div><div className="text-gray-500">{c.receipt_no}</div></td>
    <td className="px-4 py-3 text-xs"><div className="font-medium">{c.issuer}</div><div className="text-gray-500">{c.bank} · {fmtDate(c.created_at)}</div></td>
    <td className="px-4 py-3 text-xs">{c.shares.map((s) => s.delegate_name).join(", ")}</td>
    <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">{cedi(c.amount)}</td>
    <td className="px-4 py-3"><ChequeStatus s={c.status} /></td>
    <td className="px-4 py-3 text-right whitespace-nowrap space-x-3">
      {canAct && c.status === "Pending" && <>
        <button onClick={() => onAsk({ c, status: "Cleared" })} className="text-xs font-semibold text-emerald-700 hover:underline" data-testid={`cheque-clear-${c.cheque_no}`}>Cleared</button>
        <button onClick={() => onAsk({ c, status: "Bounced" })} className="text-xs font-semibold text-amber-700 hover:underline" data-testid={`cheque-bounce-${c.cheque_no}`}>Bounced</button>
      </>}
      <button onClick={() => onView(c)} className="text-xs font-semibold text-[#E60000] hover:underline" data-testid={`cheque-receipt-${c.cheque_no}`}>Receipt</button>
    </td>
  </tr>
);

export const ChequesPanel = ({ cheques, canAct, onChanged, onView }) => {
  const [ask, setAsk] = useState(null);
  const confirm = async () => {
    try { await api.patch(`/cheques/${ask.c.id}`, { status: ask.status }); toast.success(`Cheque ${ask.c.cheque_no} marked ${ask.status.toLowerCase()}`); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
    setAsk(null);
  };
  if (!cheques.length) { return null; }
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg mt-4" data-testid="cheques-panel">
      <div className="p-4 border-b font-heading font-bold">Cheques <span className="text-xs font-normal text-gray-500">· {cheques.filter((c) => c.status === "Pending").length} waiting to clear</span></div>
      <div className="overflow-x-auto"><table className="w-full text-sm">
        <thead className="bg-[#F8F9FA] text-[11px] uppercase tracking-wider text-gray-500"><tr>{["Cheque / Receipt", "Issuer", "Covers", "Amount", "Status", ""].map((h) => <th key={h} className="text-left font-bold px-4 py-3 whitespace-nowrap">{h}</th>)}</tr></thead>
        <tbody className="divide-y">{cheques.map((c) => <Row key={c.id} c={c} canAct={canAct} onAsk={setAsk} onView={onView} />)}</tbody>
      </table></div>
      <ConfirmSettle ask={ask} onClose={() => setAsk(null)} onConfirm={confirm} />
    </div>
  );
};
